export type TicketPageItem = number | 'ellipsis';

export type TicketPagination<T> = {
  items: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  rangeStart: number;
  rangeEnd: number;
  pageItems: TicketPageItem[];
};

function compactPageItems(page: number, totalPages: number): TicketPageItem[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_item, index) => index + 1);
  }

  const pages = new Set([1, totalPages, page - 1, page, page + 1]);
  const visiblePages = [...pages]
    .filter(value => value >= 1 && value <= totalPages)
    .sort((left, right) => left - right);

  return visiblePages.flatMap((value, index) => {
    const previous = visiblePages[index - 1];
    return index > 0 && value - previous > 1 ? ['ellipsis', value] : [value];
  });
}

export function paginateTicketList<T>(items: T[], requestedPage: number, requestedPageSize: number): TicketPagination<T> {
  const pageSize = Math.max(1, Math.floor(requestedPageSize));
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safeRequestedPage = Number.isFinite(requestedPage) ? Math.floor(requestedPage) : 1;
  const page = Math.min(totalPages, Math.max(1, safeRequestedPage));
  const offset = (page - 1) * pageSize;

  return {
    items: items.slice(offset, offset + pageSize),
    page,
    pageSize,
    totalItems,
    totalPages,
    rangeStart: totalItems ? offset + 1 : 0,
    rangeEnd: Math.min(offset + pageSize, totalItems),
    pageItems: compactPageItems(page, totalPages),
  };
}
